import { Paper, Tooltip, Typography } from '@mui/material'
import { HatchCoverMoveDto } from '@operations/app/api'
import { EntityTable } from '@operations/components/table/EntityTable'
import { formatStringDate } from '@operations/utils/format-date'
import { useTranslate } from '@tolgee/react'
import { BinIcon, EditIcon, IconButton } from '@tom-ui/ui'
import { PATHS } from '@tom-ui/utils'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { useNavigate } from 'react-router'

interface Props {
  data: HatchCoverMoveDto[]
  vesselVisitId: number
  onDelete: (id: number) => Promise<void>
}

export const HatchCoverMovesTable = observer(({ data, vesselVisitId, onDelete }: Props) => {
  const { t } = useTranslate()

  const navigate = useNavigate()

  const columns = useMemo<MRT_ColumnDef<HatchCoverMoveDto>[]>(
    () => [
      {
        accessorKey: 'equipmentName',
        header: t('equipment', 'Equipment'),
      },
      {
        accessorKey: 'direction',
        header: t('direction', 'Direction'),
        accessorFn: ({ direction }) => <Typography variant='body2'>{t(direction)}</Typography>,
      },
      {
        accessorKey: 'at',
        header: t('date', 'Date'),
        accessorFn: ({ at }) => (
          <Typography variant='body2' color={'secondary'}>
            {formatStringDate(at)}
          </Typography>
        ),
      },
      {
        accessorKey: 'operator',
        header: t('user', 'User'),
        accessorFn: ({ operator, operatorName }) => operatorName ?? operator,
      },
    ],
    [t],
  )

  return (
    <Paper variant='elevation' elevation={1}>
      <EntityTable
        columns={columns}
        items={data}
        isFiltering
        customizeRowActions={(row: any) => (
          <>
            <Tooltip
              arrow
              placement='left'
              title={t('editHatchCoverMove', 'Edit hatch cover move')}
            >
              <IconButton
                data-cy='edit-hatch-cover-move-btn'
                onClick={() =>
                  navigate(
                    `${PATHS.visits.vesselVisits.url}/${vesselVisitId}/${PATHS.visits.vesselVisits.hatchCovers.url}/${row.original.id}/upsert`,
                  )
                }
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip
              arrow
              placement='left'
              data-cy='delete-hatch-cover-move-btn'
              title={t('deleteHatchCoverMove', 'Delete hatch cover move')}
            >
              <IconButton onClick={() => onDelete(row.original.id)}>
                <BinIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      />
    </Paper>
  )
})
